import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  addUserToCompany,
  getAccessProjects,
  loadMoreProjects,
  getCustomers,
  deleteCustomer,
  getCustomerInfo,
  enableCustomer,
  addCustomerToCompany,
  enableProjectToCompanyUser,
  enableProjectToCompany,
  removeProjectFromCompany,
  addProjectToCompany,
  getAccessAssistants,
  loadMoreAssistants,
  setAssistantsToCompany,
  setAiChatsToCompany,
  setAiMediaToCompany,
} from './customersActions';
import selectElementInArray from '../../utils/selectElementInArray';

const initialState = {
  isUsersTableLoading: false,

  accessProjects: [],
  accessProjectsTotal: 0,
  isAccessProjectsLoading: false,

  accessAssistants: [],
  accessAssistantsTotal: 0,
  isAccessAssistantsLoading: false,

  customers: [],
  customersTotal: 0,
  isCustomersLoading: false,
  customersTableSettings: {
    page: 0,
    sort: '-createdOn',
    size: 10,
    searchQuery: '',
  },

  customerInfo: {},
  isCustomerInfoLoading: false,
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    changeCustomersTableSettings: (state, action) => {
      state.customersTableSettings = { ...state.customersTableSettings, ...action.payload };
    },
    changeCustomerUsersEnabled: (state, action) => {
      if (state.customerInfo) {
        state.customerInfo.customers = state.customerInfo.customers.map((user) =>
          user.id === action.payload.user.id
            ? { ...user, enabled: action.payload.user.enabled, actionedBy: action.payload.actionedBy }
            : user,
        );
      }
    },
    removeCustomerUsers: (state, action) => {
      if (state.customerInfo) {
        state.customerInfo.customers = state.customerInfo.customers.filter((user) => user.id !== action.payload);
      }
    },

    resetCustomerInfo: (state) => {
      state.customerInfo = {};
    },
    resetAccessProjects: (state) => {
      state.accessProjects = initialState.accessProjects;
      state.accessProjectsTotal = initialState.accessProjectsTotal;
      state.isAccessProjectsLoading = initialState.isAccessProjectsLoading;
    },
    resetCustomersData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUserToCompany.fulfilled, (state) => {
        state.isUsersTableLoading = false;
      })
      .addCase(addUserToCompany.pending, (state) => {
        state.isUsersTableLoading = true;
      })
      .addCase(addUserToCompany.rejected, (state) => {
        state.isUsersTableLoading = false;
      })

      .addCase(getAccessProjects.fulfilled, (state, action) => {
        state.accessProjects = action.payload.content;
        state.accessProjectsTotal = action.payload.total;
        state.isAccessProjectsLoading = false;
      })
      .addCase(loadMoreProjects.fulfilled, (state, action) => {
        state.accessProjects = [...state.accessProjects, ...action.payload.content];
        state.accessProjectsTotal = action.payload.total;
        state.isAccessProjectsLoading = false;
      })

      .addCase(getAccessAssistants.fulfilled, (state, action) => {
        state.accessAssistants = action.payload.content;
        state.accessAssistantsTotal = action.payload.total;
        state.isAccessAssistantsLoading = false;
      })
      .addCase(loadMoreAssistants.fulfilled, (state, action) => {
        state.accessAssistants = [...state.accessAssistants, ...action.payload.content];
        state.accessAssistantsTotal = action.payload.total;
        state.isAccessAssistantsLoading = false;
      })

      .addCase(getCustomers.fulfilled, (state, action) => {
        state.customers = action.payload.content;
        state.customersTotal = action.payload.total;
        state.isCustomersLoading = false;
      })
      .addCase(getCustomers.pending, (state) => {
        state.isCustomersLoading = true;
      })
      .addCase(getCustomers.rejected, (state) => {
        state.isCustomersLoading = false;
      })

      .addCase(deleteCustomer.fulfilled, (state) => {
        state.isCustomersLoading = false;
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.isCustomersLoading = true;
      })
      .addCase(deleteCustomer.rejected, (state) => {
        state.isCustomersLoading = false;
      })

      .addCase(enableProjectToCompanyUser.fulfilled, (state, action) => {
        const users = state.customerInfo.customers;
        state.customerInfo.customers = users.map((user) =>
          action.payload.userId === user.id
            ? {
                ...user,
                assignedProjects: selectElementInArray(
                  user.assignedProjects,
                  action.payload.projectId,
                  action.payload.enabled,
                ),
              }
            : user,
        );
      })

      .addCase(enableProjectToCompany.fulfilled, (state, action) => {
        const projects = state.customerInfo.companyProjects;
        const users = state.customerInfo.customers;

        state.customerInfo.companyProjects = projects.map((project) =>
          action.payload.projectId === project.projectId ? action.payload : project,
        );

        state.customerInfo.customers = users.map((user) =>
          user.assignedProjects.includes(action.payload.projectId)
            ? {
                ...user,
                assignedProjects: selectElementInArray(
                  user.assignedProjects,
                  action.payload.projectId,
                  action.payload.enabled,
                ),
              }
            : user,
        );
      })

      .addCase(setAssistantsToCompany.fulfilled, (state, action) => {
        state.customerInfo.assistants = action.payload;
      })

      .addCase(setAiChatsToCompany.fulfilled, (state, action) => {
        state.customerInfo.aiChatTypes = action.payload;
      })
      .addCase(setAiMediaToCompany.fulfilled, (state, action) => {
        state.customerInfo.aiPlaygroundMediaTypes = action.payload;
      })

      .addCase(getCustomerInfo.fulfilled, (state, action) => {
        state.customerInfo = action.payload;
        state.isCustomerInfoLoading = false;
      })
      .addCase(getCustomerInfo.pending, (state) => {
        state.isCustomerInfoLoading = true;
      })
      .addCase(getCustomerInfo.rejected, (state) => {
        state.isCustomerInfoLoading = false;
      })

      .addCase(addCustomerToCompany.fulfilled, (state, action) => {
        state.customerInfo.customers = [action.payload, ...state.customerInfo.customers];
        state.isCustomersLoading = false;
      })
      .addCase(addCustomerToCompany.pending, (state) => {
        state.isCustomersLoading = true;
      })
      .addCase(addCustomerToCompany.rejected, (state) => {
        state.isCustomersLoading = false;
      })

      .addCase(enableCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.map((customer) =>
          customer.id === action.payload.id ? action.payload : customer,
        );
      })

      .addCase(addProjectToCompany.fulfilled, (state, action) => {
        const projects = state.customerInfo.companyProjects;
        state.customerInfo.companyProjects = [action.payload, ...projects];
      })

      .addCase(removeProjectFromCompany.fulfilled, (state, action) => {
        const projects = state.customerInfo.companyProjects;

        state.customerInfo.companyProjects = projects.filter(({ projectId }) => action.payload !== projectId);
      })

      .addMatcher(isAnyOf(getAccessProjects.pending, loadMoreProjects.pending), (state) => {
        state.isAccessProjectsLoading = true;
      })
      .addMatcher(isAnyOf(getAccessProjects.rejected, loadMoreProjects.rejected), (state) => {
        state.isAccessProjectsLoading = false;
      })

      .addMatcher(isAnyOf(getAccessAssistants.pending, loadMoreAssistants.pending), (state) => {
        state.isAccessAssistantsLoading = true;
      })
      .addMatcher(isAnyOf(getAccessAssistants.rejected, loadMoreAssistants.rejected), (state) => {
        state.isAccessAssistantsLoading = false;
      });
  },
});

export const {
  resetCustomersData,
  changeCustomersTableSettings,
  changeCustomerUsersTableSettings,
  changeCustomerUsersEnabled,
  removeCustomerUsers,
  resetCustomerInfo,
  resetAccessProjects,
} = customersSlice.actions;

export default customersSlice.reducer;
